import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { articles, categories } from '@/mocks/jiese';
import SEOHead from '@/components/feature/SEOHead';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const currentCategory = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  const categoryArticles = useMemo(
    () => (slug ? articles.filter((a) => a.category === slug) : []),
    [slug]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!currentCategory) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>分类未找到</h1>
          <p className="text-sm text-foreground-600 mb-4">请检查分类链接是否正确</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回首页
          </button>
        </div>
      </div>
    );
  }

  const navigateTo = (path: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(path);
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 戒色完全指南`}
        description={`${currentCategory.description}。戒色完全指南为您提供关于${currentCategory.name}的科学知识与实用方法。`}
        keywords={`戒色,${currentCategory.name},戒色方法,戒色心得,戒色成功经验`}
        canonicalUrl={`https://purifypath.github.io/category/${slug}`}
        breadcrumbList={[
          { name: '首页', url: 'https://purifypath.github.io/' },
          { name: currentCategory.name, url: `https://purifypath.github.io/category/${slug}` },
        ]}
      />

      <main className="pt-16">
        {/* Category Hero */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-5 flex-wrap" aria-label="Breadcrumb">
              <button onClick={() => navigateTo('/')} className="hover:text-primary-600 transition-colors cursor-pointer">首页</button>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">{currentCategory.name}</span>
            </nav>

            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className={`${currentCategory.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{currentCategory.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200/60 sticky top-16 z-30">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-2 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => navigateTo(`/category/${cat.slug}`)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Article List */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <article
                    key={article.id}
                    className="group bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    onClick={() => navigateTo(`/article/${article.slug}`)}
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h2 className="text-base md:text-lg font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 mb-3 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {article.date}</span>
                        <span className="flex items-center gap-1"><i className="ri-time-line"></i> {article.readTime}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-article-line text-2xl"></i>
                </div>
                <p className="text-foreground-600 text-sm">该分类下暂无文章，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}